// ═══════════════════════════════════════════════════════════════════════════
// useHandoffLogin — drives the "sign me into the desktop app" buttons.
//
// Wraps openDesktopAppWithLogin with the two bits of state a button needs:
// in-flight (so it can't be double-clicked into minting two codes) and a
// human-readable error when the automatic path failed and we fell back to
// opening the app plainly.
//
// No success state: on success the desktop app signs in, the daemon probe
// notices within ~5s, and the whole page moves on by itself.
//
// Call this ONCE per page and pass the result down. Two instances mean two
// independent in-flight flags, so clicking one button leaves the other live —
// and a second click mints a second code, which the backend issues by deleting
// the user's previous unused one. The first deep link would then be dead.
// ═══════════════════════════════════════════════════════════════════════════
import { useState, useCallback, useRef, useEffect } from "react";
import { openDesktopAppWithLogin } from "./deepLink";

export default function useHandoffLogin() {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState(null);

  // The deep link can navigate/blur the tab; guard against setting state after
  // this component is gone.
  //
  // `alive` must be re-armed on mount, not just initialised once: StrictMode
  // runs effects mount → unmount → mount in development, so the cleanup fires
  // on a component that is still very much on screen. Leaving it false there
  // swallowed both setPending(false) and setError — the button stuck on
  // "SIGNING IN…" forever and never reported why.
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);

  const signIn = useCallback(async () => {
    if (pending) return;               // one code per click, not per double-click
    setPending(true);
    setError(null);

    const result = await openDesktopAppWithLogin();

    if (!alive.current) return;
    setPending(false);
    if (!result.ok) setError(result.error);
  }, [pending]);

  return { signIn, pending, error };
}
